import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PageHeader } from "../DataUpload/FileUploadAndProcessForm";
import { Grid } from "antd";
import { ArrowRight, Upload} from "lucide-react";
import RSM from "../../assets/images/chartsImages/RSM.png"
import Benford from "../../assets/images/chartsImages/Benford.png"
import RSF from "../../assets/images/chartsImages/RSF.png"
import Normality from "../../assets/images/chartsImages/NormalityThresholdScanner.png"
import Neighbourhood from "../../assets/images/chartsImages/NeighborhoodOutlierDetector.png"
import Density from "../../assets/images/chartsImages/ValueDensityIndicator.png"
import Risk from "../../assets/images/chartsImages/RiskOutcomeForecastModel.png"
import Ensemble from "../../assets/images/chartsImages/EnsembleModel.png"

/* ---------------- DATA ---------------- */

const models = [
  {
    id: "ml8",
    title: "Random Sampling Model",
    subtitle: "The Random Sampling Engine uses stratified and weighted sampling rather than blind randomness.",
    description:
      "Unlike a Simple Random Sample (SRS), this model uses stratified and weighted algorithms. It segments the population into subgroups (strata) based on risk factors—like months , amountand assigns a higher probability of selection to 'high-risk' strata",
    chartPreview: "Stratified Sampling Distribution",
    xynexIdentifies : "By uploading payroll and sales data, you select variables such as Employee ID and Incentive Amount. The model ensures that teams with high 'Order Cancellations' are audited more frequently than stable teams. ",
    genAIExplanation: "It flags that 'Manager A’s team has a 15% higher incentive payout correlated with a 20% higher return rate,' justifying why they were sampled. ",
    impactText: "Prevents manipulation the system by ensuring auditors focus on areas where leakage is most likely to occur. ",
    chart_image: RSM,
  },
  {
    id: "benford",
    title: "Benford’s Law",
    subtitle: "The Benford Intelligence Scanner goes far beyond a basic digit check.",
    description:
      "This model is based on the First-Digit Law, which states that in many naturally occurring collections of numbers, the leading digit is likely to be small (e.g., '1' appears ~30% of the time, while '9' appears <5%). Fraudulent data created by humans rarely follows this logarithmic distribution.",
    chartPreview: "Benford's Curve Analysis",
    xynexIdentifies : "Xynex ingests vendor master and invoice data, selecting Amount and Tax ID. It scans for 'unnatural' distributions of digits that suggest manual manipulation.",
    genAIExplanation: "It identifies when invoice amounts cluster around specific rounded figures or start with digits that defy Benford’s expected frequency.",
    impactText: "Useful for uncovering Duplicate Vendor Payments or Bid Rigging where financial figures have been artificially 'tailored'.",
    chart_image: Benford,
  },
  {
    id: "relative",
    title: "Relative Size Factor",
    subtitle: "The Relative Size Factor Scanner looks at how big each value is compared to everything else in the dataset.",
    description:
      "RSF calculates the ratio of the largest value in a subset to the second-largest (or average) value. A high RSF indicates a potential error or outlier that is disproportionately large compared to the rest of the group.",
    chartPreview: "RSF Ratio Plot",
    xynexIdentifies : "In manufacturing, it ingests Production Logs and GL Labor Costs. It compares the cost of individual batches against the 'Standard Cost'",
    genAIExplanation: "Batch #402 showed a 30% spike in power consumption.suggesting a mechanical calibration issue." ,
    impactText: "Reveals 'Efficiency Erosion' masked by aggregate averages, allowing for Granular Cost Control.",
    chart_image: RSF,
  },
  {
    id: "normality",
    title: "Normality Threshold Scanner",
    subtitle: "The Normality Threshold Scanner works like a smart filter that learns what is usual in your data and then points out what looks unusual.",
    description:
      "This is a Behavioral Baseline model. It uses machine learning to learn the 'normal' velocity and timing of transactions for a specific department and triggers when current activity exceeds a statistically significant threshold..",
    chartPreview: "Normal Distribution Bell Curve",
    xynexIdentifies : "It analyzes Budget Consumption Velocity. It flags 'Hockey Stick' spending patterns where a massive percentage of the budget is spent in the final 30 days of the fiscal year. ",
    genAIExplanation: "The IT Department has processed 45% of its budget in the last 15 days of March... with zero history in the previous 11 months." ,
    impactText: "Stops 'December Fever' (defensive spending) and ensures more accurate future budgeting. ",
    chart_image: Normality,
  },
  {
    id: "neighbourhood",
    title: "Neighbourhood Outlier Detector",
    subtitle: "The Neighbourhood Outlier Detection Scanner checks whether a data point behaves differently from others that look similar to it.",
    description:
      "This is a spatial or cluster-based anomaly detection model. It defines a 'neighbourhood' of similar data points (e.g., transactions from the same vendor or user) and identifies points that deviate significantly from the local mean or density.",
    chartPreview: "Local Outlier Factor Plot",
    xynexIdentifies : "It looks for 'clustering'—multiple transactions from the same user to the same vendor within a 24–72 hour window. If the sum of these 'neighbours' exceeds a threshold, they are flagged. ",
    genAIExplanation: "Three separate invoices... issued within 48 hours... the combined total is $51,000, which exceeds the $50,000 manual approval limit",
    impactText: "Effectively stops Split-PO Fraud where employees bypass senior management approval.",
    chart_image: Neighbourhood,
  },
  {
    id: "density",
    title: "Value Density Indicator",
    subtitle: "The Value Density Indicator looks at where values cluster and where they suddenly spike.",
    description:
      "This model identifies 'Financial Fingerprints' by looking for clusters of identical or near-identical values that occur across different categorical labels (like Vendor Names).",
    xynexIdentifies : "It ingests data like Invoice Date, Amount, and Bank Account Number. It flags when identical amounts hit the same bank account from two differently named vendor profiles.",
    genAIExplanation: "Two payments were made on the same date for the same amount to two different vendor profiles that share the same bank account number." ,
    impactText: "Leads to immediate cash recovery by identifying duplicate payments across different business units.",
    chart_image: Density,
  },
  {
    id: "risk",
    title: "Risk Outcome Forecast Model",
    subtitle: "The Risk Outcome Forecast Model looks ahead instead of backward.",
    description:
      "This is a Predictive Analytics model. It uses historical correlation data (e.g., past machine failures or fraud events) to identify early-warning signs in current live data.",
    chartPreview: "Risk Trend Forecast",
    xynexIdentifies : "It correlates Energy Spikes or Material Grade with future outcomes like Batch Rejection Rates. ",
    genAIExplanation: "It flags that specific mechanical calibrations are trending toward failure, or that 'Urgent' bid patterns are mirroring previous cartel behavior.",
    impactText: "Enables Predictive Maintenance—fixing issues before they become total machine breakdowns or financial losses. ",
    chart_image: Risk,
  },
  {
    id: "ensemble",
    title: "Ensemble Model",
    subtitle: "The Ensemble Mode brings multiple analytical models together to think as one.",
    description:
      "An Ensemble approach combines multiple models (like Outlier Detection + Benford’s Law + Metadata Analysis) to reduce 'false positives' and increase the detection rate of complex, multi-layered schemes.",
    chartPreview: "Ensemble Consensus Chart",
    xynexIdentifies : "In Bid Rigging, it correlates Technical Specifications, Submission Time, and IP Addresses. It looks for hidden patterns like identical typos across different proposals.",
    genAIExplanation: "Bidders A and B... share 95% identical phrasing in their technical scope, suggesting the bids were created by the same entity." ,
    impactText: "Ensures a truly competitive process by breaking up sophisticated 'Vendor-Buyer' cartels. ",
    chart_image: Ensemble,
  },
];

/* ---------------- COMPONENT ---------------- */
const { useBreakpoint } = Grid;
export default function ModelExplanationGrid({ handleGetStarted }) {
  const [active, setActive] = useState(null);
  const screens = useBreakpoint();

  return (
    <>
      {/* GRID */}
      <PageHeader screens={screens} />
      <div className="relative w-full min-h-screen bg-black flex flex-col items-center justify-center px-4 sm:px-6 py-12 rounded-2xl">
        <div className="mx-auto max-w-6xl grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {models.map((model) => (
            <motion.div
              key={model.id}
              layoutId={`card-${model.id}`}
              className="rounded-3xl bg-gradient-to-r from-pink-300 via-violet-400 to-purple-500 backdrop-blur p-6 flex flex-col justify-between shadow-lg"
              whileHover={{ y: -4 }}
            >
              {/* CONTENT */}
              <div>
                <motion.h3
                  layoutId={`title-${model.id}`}
                  className="text-xl font-bold text-gray-700 mb-2"
                >
                  {model?.title}
                </motion.h3>

                <p className="text-sm text-white/90 mb-4">{model?.subtitle}</p>
              </div>

              {/* EXPLORE CTA */}
              <button
                onClick={() => setActive(model)}
                className="mt-6 flex items-center bg-black/40 px-4 py-2 rounded-full justify-between text-sm font-semibold text-white group"
              >
                <span>Explore</span>
                <span className="ml-2 transition-transform group-hover:translate-x-1">
                  <ArrowRight />
                </span>
              </button>
            </motion.div>
          ))}
        </div>

        <button
          onClick={handleGetStarted}
          className="mt-8 px-10 py-4 rounded-xl bg-gradient-to-r from-blue-500 to-pink-500 text-white font-semibold text-base hover:scale-105 flex items-center justify-center gap-2"
        >
          <Upload className="w-5 h-5" />
          Upload your file
        </button>
      </div>

      {/* EXPANDED CARD */}
      <AnimatePresence>
        {active && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* BACKDROP */}
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setActive(null)}
            />

            {/* SIDE PANEL */}
            <motion.div
              layoutId={`card-${active.id}`}
              initial={{ x: 80, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: 80, opacity: 0 }}
              transition={{ type: "spring", stiffness: 260, damping: 25 }}
              className="relative w-[90%] md:w-full max-w-3xl max-h-[90vh] rounded-3xl bg-gradient-to-br from-[#1a0f2e] via-[#2b1450] to-[#1a0f2e] text-white shadow-2xl p-4 md:p-6 flex flex-col overflow-y-auto"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "rgba(255, 255, 255, 0.2) transparent",
              }}
            >
              {/* HEADER */}
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2 text-xl font-semibold">
                  <span className="text-yellow-400">💡</span>
                  <span>{active.title}</span>
                </div>

                <button
                  onClick={() => setActive(null)}
                  className="text-white/60 hover:text-white transition"
                >
                  ✕
                </button>
              </div>

              {/* TITLE */}
              <h3 className="text-lg font-bold mb-2">The Model Logic</h3>

              {/* DESCRIPTION */}
              <p className="text-sm text-white/70 leading-relaxed mb-5">
                {active.description}
              </p>

              {/* CHART PREVIEW */}
              <div className=" bg-black/30 rounded-2xl h-36 mb-4 ">
                <img
                  src={active?.chart_image}
                  alt="Model Chart"
                  className="w-full h-full rounded-2xl"
                />
              </div>

              {/* Xynex Identifies It */}
              <div className="mb-5">
                <div className="text-sm font-semibold mb-2">
                  How Xynex Identifies It
                </div>
                <p className="text-sm text-white/80 leading-relaxed">
                  {active.xynexIdentifies}
                </p>
              </div>

              {/* The GenAI Explanation */}
              <div className="mb-5">
                <div className="text-sm font-semibold mb-2">
                 The GenAI Explanation
                </div>
                <p className="text-sm text-white/80 leading-relaxed">
                  {active.genAIExplanation}
                </p>
              </div>

              {/* EXAMPLE BOX */}
              <div className=" bg-gradient-to-r from-pink-500/30 to-purple-500/30 rounded-2xl p-3 mb-3 border border-white/10">
                <div className="text-sm font-semibold mb-2">The Impact</div>

                <p className="text-sm text-white/80 leading-relaxed">
                  {active.impactText}
                </p>
              </div>

              {/* CLOSE BUTTON */}
              <button
                onClick={() => setActive(null)}
                className=" mt-auto bg-gradient-to-r from-blue-500 to-pink-500 rounded-xl py-3 font-semibold text-sm hover:scale-[1.02] transition"
              >
                Close
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
